import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { signOut } from 'next-auth/react';
import { Heading, ButtonLink } from '../../atoms';
import { useSessionCustomer, useAddress } from '../../customer';
import { AccountHeader } from '../AccountHeader';
import { FOOTER_CONTENT } from '../../testing/constants';
import { LOGGEDIN_LINKS } from '../../molecules';
import { useReporter } from '../../reporter';
import { RedirectToLogin } from '../../router';
import { usePayment } from '../../payment';
import { useCurrentPet } from '../useCurrentPet';
import { EditMyAccountInfoForm } from '../EditMyAccountInfoForm';
import { passwordInfo } from '../EditMyAccountInfoForm/constants';
import { LoadingView, Footer, BrightbackCancelButton } from '../../components';
import {
  Container,
  HeadingContainer,
  LinkContainer,
  StyledInfoContainer,
  VerticalSpacer,
} from './styles';
import {
  userViewsMyInfo,
  clickEditPassword,
  clickEditDelivery,
  clickEditPayment,
  clickLogout,
} from './events';

export const MyInfoContainer = ({
  paymentTitleLabel,
  paymentPageUrl,
  paymentButtonLabel,
  addressTitleLabel,
  addressPageUrl,
  addressButtonLabel,
  passwordTitleLabel,
  passwordPageUrl,
  passwordButtonLabel,
  headerTitleLabel,
}) => {
  const reporter = useReporter();

  useEffect(() => {
    reporter.tag(userViewsMyInfo());
  }, [reporter]);

  const {
    customer,
    isActive,
    isLoading: isCustomerLoading,
  } = useSessionCustomer();
  const { pet, isLoading: isPetLoading } = useCurrentPet();
  const { address } = useAddress(customer?.id);
  const { payment } = usePayment(customer?.id);
  let deliveryInfo = [];
  let paymentInfo = [];

  const isLoading = isCustomerLoading || isPetLoading;

  if (!isLoading && !isActive) {
    return <RedirectToLogin />;
  }

  if (isLoading) {
    return <LoadingView />;
  }

  if (payment) {
    paymentInfo = [
      'Payment method',
      `${payment.brand} *****${payment.last_four_digits}`,
    ];
  }

  if (address) {
    deliveryInfo = [
      address.address1,
      address.address2,
      `${address.city}, ${address.state} ${address.zip_code}`,
    ].filter(Boolean);
  }

  const logOut = () => {
    const { Gladly } = window;
    reporter.tag(clickLogout());
    if (Gladly) Gladly.clearUser();
    signOut();
  };
  const userAccountInformation = [
    {
      title: passwordTitleLabel,
      href: passwordPageUrl,
      infoList: passwordInfo,
      buttonLabel: passwordButtonLabel,
      onInfoClick: () => reporter.tag(clickEditPassword()),
    },
    {
      title: addressTitleLabel,
      href: addressPageUrl,
      infoList: deliveryInfo,
      buttonLabel: addressButtonLabel,
      onInfoClick: () => reporter.tag(clickEditDelivery()),
    },
    {
      title: payment ? paymentTitleLabel : 'Add new payment method',
      href: paymentPageUrl,
      infoList: paymentInfo,
      buttonLabel: paymentButtonLabel,
      onInfoClick: () => reporter.tag(clickEditPayment()),
    },
  ];

  const brightbackInfo = {
    customerId: customer.id,
    customerEmail: customer.email,
    petSubscriptionId: pet.subscription_id,
  };

  return (
    <StyledInfoContainer>
      <AccountHeader
        links={LOGGEDIN_LINKS}
        logoUrl="/"
        logoSrText="Ollie logo"
        name={customer.first_name}
      />
      <Container>
        <HeadingContainer>
          <Heading
            headingLevel="h3"
            typography="heading1"
            headingText={headerTitleLabel}
            position="center"
          />
        </HeadingContainer>
        <EditMyAccountInfoForm
          userAccountInformation={userAccountInformation}
        />
        <LinkContainer>
          <ButtonLink variant="hero.cranberry" onClick={logOut}>
            Logout
          </ButtonLink>
          <VerticalSpacer />
          <BrightbackCancelButton {...brightbackInfo} />
        </LinkContainer>
      </Container>
      <Footer {...FOOTER_CONTENT} />
    </StyledInfoContainer>
  );
};

MyInfoContainer.displayName = 'MyInfoContainer';
MyInfoContainer.propTypes = {
  paymentTitleLabel: PropTypes.string.isRequired,
  paymentPageUrl: PropTypes.string.isRequired,
  paymentButtonLabel: PropTypes.string.isRequired,
  addressTitleLabel: PropTypes.string.isRequired,
  addressPageUrl: PropTypes.string.isRequired,
  addressButtonLabel: PropTypes.string.isRequired,
  passwordTitleLabel: PropTypes.string.isRequired,
  passwordPageUrl: PropTypes.string.isRequired,
  passwordButtonLabel: PropTypes.string.isRequired,
  headerTitleLabel: PropTypes.string.isRequired,
};
